import capitalize from 'lodash/capitalize';
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { createContext } from 'use-context-selector';

import type { CandidateContextActions, CandidateContextProps, CandidateContextProviderProps } from './types';

import { default as ConfirmJobboardCreditUsage } from 'components/ui/molecules/ConfirmJobboardCreditUsage';
import { default as Modal } from 'components/ui/molecules/Modal';
import NoCreditsAvailableForMonsterboardModalContent from 'components/ui/molecules/NoCreditsAvailableForMonsterboardModalContent';
import { ShouldInstallBrowserExtensionDialog } from 'components/ui/molecules/ShouldInstallBrowserExtensionDialog/ShouldInstallBrowserExtensionDialog';
import { UserContext } from 'shared/legacy/contexts/UserContext/UserContext';
import { NoCreditsAvailableForMonsterboard } from 'shared/legacy/exceptions';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { useCreateSnackbar } from '../SnackbarContext/useCreateSnackbar';
import { useActions } from './actions';
import { isLoadingInitialState as actionsIsLoadingInitialState } from './actions/useActions';
import { useShouldUnlockModal } from './actions/useShouldUnlockModal';
import { CandidateEmailSettingsContextProvider } from './CandidateEmailSettingsContext';
import { useCandidateEffects } from './effects';
import { useOutreach } from './outreach';
import { useProps } from './props';
import { initialState as searchv3InitialState, useSearchV3Candidate } from './searchv3/useSearchV3Candidate';

const initialState: CandidateContextProps = {
  candidate: null as any,
  candidateEsPersonId: '',
  candidateName: '',
  campaignId: undefined,
  notObfuscatedPhone: undefined,
  notObfuscatedEmail: undefined,
  isNoLongerAvailable: false,
  isAlreadyInteracted: false,
  activeJobboard: undefined,
  labels: [],
  availableActions: {
    actions: [],
    extraOptions: {},
  },
  onUnlock: () => {
    throw '[CandidateContext - onUnlock] Provide CandidateContextProvider';
  },
  onSaveLabels: () => {
    throw '[CandidateContext - onSaveLabels] Provide CandidateContextProvider';
  },
  onApprove: () => {
    throw '[CandidateContext - onApprove] Provide CandidateContextProvider';
  },
  onContact: () => {
    throw '[CandidateContext - onContact] Provide CandidateContextProvider';
  },
  onShortlist: () => {
    throw '[CandidateContext - onShortlist] Provide CandidateContextProvider';
  },
  onShare: () => {
    throw '[CandidateContext - onShare] Provide CandidateContextProvider';
  },
  onResendAts: () => {
    throw '[CandidateContext - onResendAts] Provide CandidateContextProvider';
  },
  onReject: () => {
    throw '[CandidateContext - onReject] Provide CandidateContextProvider';
  },
  onMakeInvisible: () => {
    throw '[CandidateContext - onMakeInvisible] Provide CandidateContextProvider';
  },
  onRefresh: () => {
    throw '[CandidateContext - onRefresh] Provide CandidateContextProvider';
  },
  onSetActiveJobBoard: () => {
    throw '[CandidateContext - onSetActiveJobBoard] Provide CandidateContextProvider';
  },
  onRJComm: () => {
    throw '[CandidateContext - onRJComm] Provide CandidateContextProvider';
  },
  outreachMethods: {
    shouldEnableCall: false,
    shouldEnableEmail: false,
    shouldEnableWhatsapp: false,
    shouldEnableLinkedin: false,
    shouldEnableLinkedinPro: false,
    shouldEnableIndeed: false,
    shouldEnableWerknl: false,
    shouldEnableRJCommHub: false,
  },
  availableOutreachMethods: [],
  outreachInformationWhyNotEnabled: {
    whyLinkedinNotEnabled: undefined,
    whyLinkedinProNotEnabled: undefined,
    whyIndeedNotEnabled: undefined,
    whyEmailNotEnabled: undefined,
    whyWhatsappNotEnabled: undefined,
    whyCallNotEnabled: undefined,
    whyWerknlNotEnabled: undefined,
    whyRJCommHubNotEnabled: undefined,
    disabledError: {
      linkedin: undefined,
      indeed: undefined,
      email: undefined,
      whatsapp: undefined,
      call: undefined,
      werknl: undefined,
      rjCommHub: undefined,
    },
  },
  personalizationTokens: {
    '{firstName}': '',
    '{fullName}': '',
    '{lastCompany}': '',
    '{lastName}': '',
    '{lastPosition}': '',
    '{recruiterFullName}': '',
    '{nameJobboard}': '',
    '{nameVacancy}': '',
    '{location}': '',
    '{schedulerLink}': '',
  },
  isLoading: actionsIsLoadingInitialState,
  shouldShowRequestContactInfo: false,
  lastRefresh: '',
  searchv3: searchv3InitialState,
  setShouldShowInstallBrowserExtensionModal: () => {
    throw '[CandidateContext - setShouldShowInstallBrowserExtensionModal] Provide CandidateContextProvider';
  },
  setShouldShowOutreachModal: () => {
    throw '[CandidateContext - setShouldShowOutreachModal] Provide CandidateContextProvider';
  },
};

export const CandidateContext = createContext<CandidateContextProps>(initialState);

export const CandidateContextProvider = memo(
  ({ candidate, children, config }: CandidateContextProviderProps) => {
    const { open } = useGlobalDialog();
    const createSnackbar = useCreateSnackbar();
    const { hasMonsterboardV2License } = useCandidateContextMemoized();
    const [shouldShowModalOfNoCreditsAvailableForMonsterboard, setShouldShowModalOfNoCreditsAvailableForMonsterboard] =
      useState(false);

    const [shouldShowInstallBrowserExtensionModal, setShouldShowInstallBrowserExtensionModal] = useState(false);

    const shouldShowRequestContactInfo =
      (hasMonsterboardV2License && candidate?.activeJobBoard === 'monsterboard_v2' && !candidate.isUnlocked) || false;

    const props = useProps(candidate);
    const outreach = useOutreach(candidate);
    const actions = useActions(candidate, {
      shouldConfirmUnlockIntention: shouldShowRequestContactInfo,
    });
    const shouldUnlockModal = useShouldUnlockModal(actions);
    const searchv3Candidate = useSearchV3Candidate(candidate, { actions, config });

    useCandidateEffects(candidate, { actions });

    const onUnlockWithCheckIfUserHasCredits: CandidateContextActions['onUnlock'] = useCallback(
      async (params) => {
        return actions.onUnlock(params).catch((error) => {
          if (error instanceof NoCreditsAvailableForMonsterboard) {
            setShouldShowModalOfNoCreditsAvailableForMonsterboard(true);
          }

          return Promise.resolve(false);
        });
      },
      [actions.onUnlock, props.activeJobboard],
    );

    const setShouldShowOutreachModal: CandidateContextProps['setShouldShowOutreachModal'] = useCallback(
      (type) => {
        if (type && candidate) {
          open(GlobalDialogType.CandidateOutreach, {
            payload: {
              candidate,
              outreachType: type,
            },
          });
        }

        if (!type) createSnackbar('Outreach type is required', { variant: 'danger', duration: 3000 });
        if (!candidate) createSnackbar('Candidate is required', { variant: 'danger', duration: 2000 });
      },
      [open, candidate],
    );

    const valueMemoized = useMemo<CandidateContextProps>(() => {
      return {
        candidate,
        searchv3: searchv3Candidate,
        config,

        shouldShowRequestContactInfo,
        candidateEsPersonId: props.candidateEsPersonId,
        candidateName: props.candidateName,
        campaignId: props.campaignId,
        notObfuscatedPhone: props.notObfuscatedPhone,
        notObfuscatedEmail: props.notObfuscatedEmail,
        labels: props.labels,
        email: props.email,
        phone: props.phone,
        activeJobboard: props.activeJobboard,
        lastRefresh: props.lastRefresh,
        isNoLongerAvailable: props.isNoLongerAvailable,
        isAlreadyInteracted: props.isAlreadyInteracted,

        outreachMethods: outreach.outreachMethods,
        availableOutreachMethods: outreach.availableOutreachMethods,
        personalizationTokens: outreach.personalizationTokens,
        outreachInformationWhyNotEnabled: outreach.outreachInformationWhyNotEnabled,

        availableActions: actions.availableActions,
        isLoading: actions.isLoading,
        onContact: actions.onContact,
        onApprove: actions.onApprove,
        onShortlist: actions.onShortlist,
        onUnlock: onUnlockWithCheckIfUserHasCredits,
        onSaveLabels: actions.onSaveLabels,
        onShare: actions.onShare,
        onResendAts: actions.onResendAts,
        onReject: actions.onReject,
        onMakeInvisible: actions.onMakeInvisible,
        onRefresh: actions.onRefresh,
        onSetActiveJobBoard: actions.onSetActiveJobBoard,
        onRJComm: actions.onRJComm,

        setShouldShowInstallBrowserExtensionModal,
        setShouldShowOutreachModal,
      };
    }, [
      candidate,
      searchv3Candidate,
      shouldShowRequestContactInfo,
      props,
      outreach,
      actions,
      onUnlockWithCheckIfUserHasCredits,
      config,
    ]);

    return (
      <>
        <CandidateContext.Provider value={valueMemoized}>
          <CandidateEmailSettingsContextProvider>{children}</CandidateEmailSettingsContextProvider>
        </CandidateContext.Provider>

        <Modal visible={shouldUnlockModal.showJobboardCreditUsageModal} hideCloseButton>
          <ConfirmJobboardCreditUsage
            onCancel={() => shouldUnlockModal.onRequestCloseConfirmUnlockModal(false)}
            onConfirm={() => shouldUnlockModal.onRequestCloseConfirmUnlockModal(true)}
            onChangeShouldSkipNextTime={shouldUnlockModal.setShouldSkipJobboardModalVerificationNextTime}
            platform={capitalize(candidate?.activeJobBoard) ?? ''}
          />
        </Modal>

        <Modal visible={shouldShowModalOfNoCreditsAvailableForMonsterboard} hideCloseButton>
          <NoCreditsAvailableForMonsterboardModalContent
            onCancel={() => setShouldShowModalOfNoCreditsAvailableForMonsterboard(false)}
          />
        </Modal>

        <ShouldInstallBrowserExtensionDialog
          isVisible={shouldShowInstallBrowserExtensionModal}
          onConfirm={() => setShouldShowInstallBrowserExtensionModal(false)}
        />
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.candidate === nextProps.candidate &&
    prevProps.config?.candidateDetails.initialTab === nextProps.config?.candidateDetails.initialTab,
);

const useCandidateContextMemoized = () => {
  const { hasMonsterboardV2License } = useContext(UserContext);

  return useMemo(() => ({ hasMonsterboardV2License }), [hasMonsterboardV2License]);
};
